import { useCallback, useMemo } from 'react';
import useFilter from './use-filter';
import type {
  Join,
  Operator,
  QueryDefinition,
  QueryValue,
} from '@/app/components/query-builder/types';

export const queryOperatorToWire: Record<Operator, string> = {
  '=': 'eq',
  '!=': 'not_in',
  '<': 'lt',
  '>': 'gt',
  '<=': 'lte',
  '>=': 'gte',
  contains: 'contains',
  beginsWith: 'starts_with',
  endsWith: 'contains',
  doesNotContain: 'not_in',
  doesNotBeginWith: 'not_in',
  doesNotEndWith: 'not_in',
  null: 'is_null',
  notNull: 'is_not_null',
  in: 'in',
  notIn: 'not_in',
  between: 'between',
  notBetween: 'not_in',
  inLast: 'in_last',
};

const operatorFromWire: Record<string, Operator> = {
  eq: '=',
  not_in: 'notIn',
  lt: '<',
  gt: '>',
  lte: '<=',
  gte: '>=',
  contains: 'contains',
  starts_with: 'beginsWith',
  is_null: 'null',
  is_not_null: 'notNull',
  in: 'in',
  between: 'between',
};

export type QueryBuilderUrlFilter = {
  field: string;
  operator: string;
  value: QueryValue;
  join?: Join;
  children?: QueryBuilderUrlFilter[];
};

const emptyQuery = (): QueryDefinition => ({
  groups: [
    {
      id: crypto.randomUUID(),
      join: 'AND',
      rules: [{ id: crypto.randomUUID(), join: 'AND', field: '', operator: '=', value: '' }],
    },
  ],
});

const filtersToQuery = (filters: QueryBuilderUrlFilter[]): QueryDefinition => ({
  groups: [
    {
      id: 'query-filter-group',
      join: 'AND',
      rules: filters.map((filter, index) => ({
        id: `query-filter-rule-${index}`,
        join: filter.join || 'AND',
        field: filter.field,
        operator: operatorFromWire[filter.operator] || '=',
        value: filter.value,
        children: filter.children?.map((child, childIndex) => ({
          id: `query-filter-rule-${index}-child-${childIndex}`,
          join: child.join || 'AND',
          field: child.field,
          operator: operatorFromWire[child.operator] || '=',
          value: child.value,
        })),
      })),
    },
  ],
});

const queryToFilters = (query: QueryDefinition): QueryBuilderUrlFilter[] =>
  query.groups.flatMap(group =>
    group.rules.map(rule => ({
      field: rule.field,
      operator: queryOperatorToWire[rule.operator],
      value: rule.value,
      ...(rule.join === 'OR' ? { join: rule.join } : {}),
      ...(rule.children?.length
        ? {
            children: rule.children.map(child => ({
              field: child.field,
              operator: queryOperatorToWire[child.operator],
              value: child.value,
            })),
          }
        : {}),
    }))
  );

export default function useQueryBuilderFilter(paramName = 'filters') {
  const filterUiMap = useMemo(
    () => ({ [paramName]: { label: 'Filter', type: 'query-builder' } }),
    [paramName]
  );
  const { filter, setFilter, clearFilters } = useFilter([paramName], filterUiMap);
  const fallbackQuery = useMemo(emptyQuery, []);
  const urlFilters = filter[paramName];
  const query = useMemo(
    () =>
      Array.isArray(urlFilters)
        ? filtersToQuery(urlFilters as QueryBuilderUrlFilter[])
        : fallbackQuery,
    [fallbackQuery, urlFilters]
  );
  const setQuery = useCallback(
    (nextQuery: QueryDefinition) => setFilter(paramName, queryToFilters(nextQuery)),
    [paramName, setFilter]
  );

  return { query, setQuery, clearFilters };
}
